<template>
  <b-row>
    <b-col>
      <card>

        <template #header>
          <div class="d-flex align-items-center justify-content-between">
            <h1 class="card-title">Voicemails</h1>

            <div v-if="!routeVoicemailId">
              <b-form-group>
                <b-form-radio-group v-model="selectedView" :options="selectViewOptions" button-variant="primary"
                  buttons />
              </b-form-group>
            </div>
          </div>
        </template>

        <div class="mb-2">
          <template v-if="selectedView === 'Incomplete'">

            <b-row v-if="pharmacist" align-v="center">
              <b-col>
                Pharmacist on call: {{ pharmacist }}
              </b-col>
            </b-row>
            <b-row v-if="!routeVoicemailId">
              <b-col>
                <EnumSelector v-model="selectedTeam" label="Team" :enum-codes="teams" />
              </b-col>
            </b-row>

          </template>
          <template v-else>

            <div class="mb-3">
              <DateRangeFilter v-model="createdDateRangeFilter" label="Received Date:" />
            </div>

          </template>
        </div>

        <div v-for="item in pagedVoicemails" :key="`${item.id}`">
          <card plain style="cursor:pointer">
            <h6 @click="toggleInfo(item)">{{ item.id }}: Received {{ relativeTime(item) }}</h6>
            <b-collapse :id="`accordion-${item.id}`" accordion="my-accordion" role="tabpanel"
              :ref="`accordion-${item.id}`" style="cursor:auto">
              <b-row>
                <b-col>
                  <b-row v-show="item?.phoneNumber?.length ?? 0 > 0">
                    <b-col>
                      <p><b>Phone Number:</b> {{ item.phoneNumber }}</p>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <h6 style="font-weight: 500">
                        Transcription
                        <i class="fas fa-download" style="color: blue; cursor:pointer"
                          @click="downloadVoicemail(item.id)"></i>
                      </h6>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <audio :id="`audio-${item.id}`" controls hidden>
                        <source :id="`source-${item.id}`" type="audio/wav" />
                      </audio>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <transcription-viewer v-if="selectedVoicemail == item.id" entityType="1" :entityId="item.id"
                        @transcriptclick="transcriptClicked" />
                      {{ item.transcript }}
                    </b-col>
                  </b-row>
                </b-col>

                <b-col>
                  <EnumSelector v-model="triageLocation" label="Triage To" :enum-codes="teams" />
                  <b-button class="float-right" variant="info" @click="triageVoicemail(item)">Triage</b-button>
                  <br />
                  <hr style="width: 50%; text-align: left; margin-left: 0" />
                  <br />

                  <SearchComponent :id="`patient-search-${item.id}`" v-model="patients[item.id]" label="Patient"
                    search-u-r-l="/Patient" :initial-search-value="item.phoneNumber" prepopulate autoselect clearable />

                  <router-link v-if="patients[item.id]?.id"
                    :to="{ name: 'PatientDashboard', params: { id: patients[item.id].id } }" class="ml-2">
                    <span>Go To Profile</span> <i class="fas fa-user" />
                  </router-link>

                  <b-button class="float-right" variant="success" @click="completeVoicemail(item)">
                    Complete
                  </b-button>
                </b-col>
              </b-row>
            </b-collapse>
          </card>
        </div>

        <BottomPagerBar v-model="pageNumber" item-name="voicemail" :per-page="pageSize"
          :total-rows="filteredVoicemails.length" :loading="loading" show-total show-page-size-selector
          @change-page-size="pageSize = $event" />
      </card>
    </b-col>
  </b-row>
</template>
<script lang="ts">
import Component from "vue-class-component";
import SearchComponent from "@/components/SearchComponent.vue";
import EnumSelector from "@/components/EnumSelector.vue";
import Axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import Vue from "vue";
import { VoicemailTeam, Voicemail } from "@/models/Voicemail";
import { Watch } from "vue-property-decorator";
import moment from "moment";
import { Patient } from "@/models/Patient";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import TranscriptionViewer from "@/components/TranscriptionViewer.vue";
import BottomPagerBar from "@/components/BottomPagerBar.vue";

@Component({
  components: {
    BottomPagerBar,
    SearchComponent,
    EnumSelector,
    DateRangeFilter,
    TranscriptionViewer,
  },
})
export default class VoicemailsPage extends Vue {
  private patients: { [key: number]: Patient } = {};
  private selectedTeam: VoicemailTeam = VoicemailTeam.untriaged;
  private triageLocation: Number = 0;
  protected teams = VoicemailTeam;
  private voicemails: Voicemail[] = [];
  private pharmacist: string = '';
  private selectedView: string = 'Incomplete';
  private selectedVoicemail: number = 0;
  private loading = false;

  private createdDateRangeFilter: any = { startDate: moment().subtract(90, "days").toDate(), endDate: new Date() };
  private counts = { incomplete: 0, complete: 0 };

  protected pageNumber: number = 1;
  protected pageSize: number = 25;

  get routeVoicemailId(): number {
    const res = parseInt(this.$route.params.id);
    if (isNaN(res)) return 0;
    return res;
  }

  mounted() {
    this.getOnCallPharmacist();
    this.getCounts();

    if (this.routeVoicemailId) {
      this.getSingleVoicemail();
      return;
    }

    this.getVoicemails();
  }

  relativeTime(item: Voicemail): string {
    const voicemailReceivedTime = moment.utc(item.received);
    return `${voicemailReceivedTime.fromNow()} (${voicemailReceivedTime.local().format("L LT")})`;
  }

  toggleInfo(item: Voicemail) {
    this.selectedVoicemail = item.id;
    // console.log("toggle info", item);

    // Initialize patient for this voicemail if it doesn't exist
    if (!this.patients[item.id]) {
      this.$set(this.patients, item.id, new Patient());
    }

    const idToExpand = `accordion-${item.id}`;

    (this.$refs[idToExpand] as any)[0].show = false;

    this.$root.$emit("bv::toggle::collapse", idToExpand);
  }

  autoselect(objects: Patient[]) {
    if (objects.length > 1) {
      // let searchComponent: any = document.getElementById(`audio-${itemId}`);
      return undefined;
    }
    if (objects.length > 0) return objects[0];
    return undefined;
  }

  get pagedVoicemails() {
    const startIndex = this.pageSize * this.pageNumber - this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredVoicemails.slice(startIndex, endIndex);
  }

  get filteredVoicemails() {
    if (this.loading) return [];

    if (this.selectedView === "Incomplete" || !this.createdDateRangeFilter.startDate) {
      return this.voicemails;
    }

    return this.voicemails.filter(i => {
      let received = i.received;
      if (!received) return false;
      received = moment(i.received).toDate();

      return (received >= moment(this.createdDateRangeFilter.startDate).toDate()) &&
        (received < moment(this.createdDateRangeFilter.endDate).add(1, "day").toDate());
    })
  }

  async getSingleVoicemail() {
    try {
      const response = await Axios.get<Voicemail>(`/Voicemail/${this.routeVoicemailId}`);
      const voicemail = Object.assign(new Voicemail(), response.data);
      this.voicemails = [voicemail];

      // Initialize empty Patient object for the voicemail to ensure SearchComponent works correctly
      if (!this.patients[voicemail.id]) {
        this.$set(this.patients, voicemail.id, new Patient());
      }

      // console.log(this.voicemails);
      this.$nextTick(() => {
        this.toggleInfo(voicemail);
      });
    } catch (error) {
      console.error("Problem loading voicemail information by route Id", {
        error,
        response: (error as any)?.response
      });
      this.$notification(NotificationOptions.error(error));
    }
  }

  @Watch("selectedTeam")
  @Watch("selectedView")
  async getVoicemails() {
    try {
      this.loading = true;

      let response: AxiosResponse<Voicemail[]>;

      if (this.selectedView === "Incomplete") {
        response = await Axios.get<Voicemail[]>(`/Voicemail/Incomplete/${this.selectedTeam}`);
      } else {
        response = await Axios.get<Voicemail[]>(`/Voicemail/Completed`);
      }

      this.voicemails = response.data?.map(value => new Voicemail(value));

      // Initialize empty Patient objects for each voicemail to ensure SearchComponent works correctly
      this.voicemails.forEach(voicemail => {
        if (!this.patients[voicemail.id]) {
          this.$set(this.patients, voicemail.id, new Patient());
        }
      });
    } catch (error) {
      let errorMessage = "Error Fetching Voicemails";
      const err = error as any;
      if (err && err?.response?.data) {
        errorMessage += ` - ${err.response.data}`;
      }
      this.$notification(NotificationOptions.notificationOptionsPreset(errorMessage, NotificationOptions.NotificationTypes.danger));
    } finally {
      this.loading = false;
    }
  }

  async triageVoicemail(item: Voicemail) {
    try {
      await Axios.post(`/Voicemail/Triage/${item.id}/${this.triageLocation}`);
      await this.getVoicemails();
    } catch (error) {
      this.$notification(NotificationOptions.errorSaveNotificationPreset("Voicemail Triage", error as any));
    }
  }

  goToPatient(patient: Patient) {
    this.$router.push(`/Patient/${patient.id}`);
  }

  async completeVoicemail(item: Voicemail) {
    const patientId = Number(this.patients[item.id]?.id);
    if (!Number.isNaN(patientId)) {
      item.patientId = patientId;
    }

    try {
      await Axios.post(`Voicemail/Complete`, item);
      this.$delete(this.patients, item.id);
      await this.getVoicemails();
    } catch (error) {
      this.$notification(NotificationOptions.errorSaveNotificationPreset("Voicemail Complete", error as any));
    }
  }

  async downloadVoicemail(itemId: number) {
    try {
      const config: AxiosRequestConfig = {
        responseType: 'blob'
      }
      let response = await Axios.get(`/Voicemail/DownloadAudio/${itemId}`, config);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "audio/wav" }));
      let sourceItem: any = document.getElementById(`source-${itemId}`);
      sourceItem.src = url;
      let audioItem: any = document.getElementById(`audio-${itemId}`);
      audioItem.hidden = false;
      audioItem.pause();
      audioItem.load();
      audioItem.play();
      // console.log(response);
    } catch (err) {
      this.$notification(NotificationOptions.error(err));
    }

  }

  transcriptClicked(voicemailId: number, startTime: any) {
    let timespan = moment.duration(startTime);
    let audioItem: any = document.getElementById(`audio-${voicemailId}`);
    if (audioItem.getAttribute('hidden')) {
      this.downloadVoicemail(voicemailId);
    }
    audioItem.currentTime = timespan.seconds();
  }

  async getOnCallPharmacist() {
    const response = await Axios.get(`/Voicemail/OnCallPharmacist`);
    this.pharmacist = response.data;
  }

  async getCounts() {
    const response = await Axios.get("/Voicemail/IncompleteCompleteCounts");
    this.counts = response.data;
  }

  get selectViewOptions() {
    return [
      { text: this.counts.incomplete ? `Incomplete (${this.counts.incomplete})` : "Incomplete", value: 'Incomplete' },
      { text: this.counts.complete ? `Complete (${this.counts.complete})` : "Complete", value: 'Complete' }
    ];
  }
}
</script>

<style scoped></style>>
